// Satellite passes (GET devices/<mac>/overpasses/, POST .../schedule/): the
// server's answer read into what the card shows. Every value is checked, so
// anything that is not the endpoint's answer reads as null.

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v) => (typeof v === "string" ? v : null);

export function readPass(p) {
  if (!p || typeof p !== "object" || !str(p.satellite) || !str(p.utc)) return null;
  return {
    satellite: p.satellite,
    family: str(p.family),
    utc: p.utc,
    t: num(p.t),
    local: str(p.local),
    offNadirKm: num(p.off_nadir_km),
    viewAngle: num(p.view_angle_deg),
    halfSwathKm: num(p.half_swath_km),
    inSchedule: !!p.in_schedule,
    why: str(p.why),
    direction: p.direction === "ascending" || p.direction === "descending" ? p.direction : null,
    incidenceDeg: num(p.incidence_deg),
    edge: p.edge === true,
    esa: p.esa === "planned" || p.esa === "not_planned" ? p.esa : null,
    track: num(p.track),
    // Whether its packet takes the photo pair: not when the sun is down at the node (the server's sun_deg).
    photo: p.photo !== false,
    sunDeg: num(p.sun_deg),
  };
}

export function readOverpasses(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.passes)) return null;
  const o = data.orbits && typeof data.orbits === "object" ? data.orbits : null;
  const problem = data.problem && typeof data.problem === "object" ? data.problem : null;
  return {
    days: num(data.days),
    tzHours: num(data.tz_hours),
    passes: data.passes.map(readPass).filter(Boolean),
    orbits: o
      ? { ageH: num(o.age_h), stale: !!o.stale, error: str(o.error), fetchedAt: str(o.fetched_at) }
      : null,
    problem: problem ? { code: str(problem.code), detail: str(problem.detail) || "Passes unavailable" } : null,
  };
}

export function readPlan(data) {
  if (!data || typeof data !== "object" || typeof data.sched !== "string") return null;
  return {
    sched: data.sched,
    current: str(data.current) || "",
    describe: Array.isArray(data.describe) ? data.describe.filter((w) => typeof w === "string") : [],
    added: (Array.isArray(data.added) ? data.added : []).map(readPass).filter(Boolean),
    skipped: (Array.isArray(data.skipped) ? data.skipped : []).map(readPass).filter(Boolean),
    unchanged: !!data.unchanged,
    sent: !!data.sent,
    dryRun: !!data.dry_run,
    days: num(data.days),
    status: str(data.settings?.status),
  };
}

// "UTC+7"
export const zoneText = (h) =>
  h === null || h === undefined ? "" : `UTC${h >= 0 ? "+" : "−"}${Math.abs(h)}`;

// "Mon 26 Oct, 10:35:22" from the server's local "2026-10-26 10:35:22"
export function localText(local) {
  if (!local) return "–";
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(local);
  if (!m) return local;
  const day = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const name = day.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
  return `${name.replace(",", "")}, ${m[4]}`;
}

// "in 2 d 3 h", "in 45 min", "now"
export function untilText(t, now = Date.now()) {
  if (t === null || t === undefined) return "";
  const s = t - now / 1000;
  if (s < 60) return "now";
  const h = Math.floor(s / 3600);
  if (h < 1) return `in ${Math.round(s / 60)} min`;
  if (h < 48) return `in ${h} h`;
  return `in ${Math.floor(h / 24)} d ${h % 24} h`;
}

export const orbitsText = (o) => {
  if (!o) return "";
  if (o.ageH === null) return "Orbits: CelesTrak, age unknown.";
  const age = o.ageH < 48 ? `${Math.round(o.ageH)} h` : `${Math.round(o.ageH / 24)} days`;
  return `Orbits from CelesTrak, ${age} old${o.stale ? ` (a refresh failed${o.error ? `: ${o.error}` : ""}; still within seconds for a week or two)` : ""}.`;
};

// What each scheduled capture brought back (GET devices/<mac>/overpasses/results/), newest first.
const STATUSES = ["done", "waiting", "missed"];

export function readResult(r) {
  if (!r || typeof r !== "object" || num(r.t) === null || !STATUSES.includes(r.status)) return null;
  const photo =
    r.photo && typeof r.photo === "object"
      ? {
          ok: num(r.photo.ok) ?? 0,
          of: num(r.photo.of) ?? 0,
          frames: (Array.isArray(r.photo.frames) ? r.photo.frames : [])
            .filter((f) => f && num(f.id) !== null && str(f.thumbnail_url))
            .map((f) => ({ id: f.id, camera: str(f.camera) || "", thumbnail_url: f.thumbnail_url, expired: !!f.expired })),
        }
      : null;
  const gnss =
    r.gnss && typeof r.gnss === "object"
      ? { fresh: r.gnss.fresh === true, sats: num(r.gnss.sats), hdop: num(r.gnss.hdop) }
      : null;
  const burst =
    r.burst && typeof r.burst === "object" ? { ok: r.burst.ok === true, state: str(r.burst.state) } : null;
  const pass = r.pass && typeof r.pass === "object" && str(r.pass.satellite) ? { satellite: r.pass.satellite } : null;
  return {
    t: r.t,
    local: str(r.local),
    status: r.status,
    tasks: str(r.tasks) || "",
    dropped: str(r.dropped) || "",
    lateMs: num(r.late_ms),
    photo,
    gnss,
    burst,
    uploadDelayS: num(r.upload_delay_s),
    pass,
  };
}

export function readResults(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.results)) return null;
  return { days: num(data.days), results: data.results.map(readResult).filter(Boolean) };
}

const TASK_NAMES = { p: "photo pair", g: "GNSS fix", b: "radar burst", u: "upload" };

// "40 s", "12 min", "1 h 40 min"
export function spanText(s) {
  const secs = Math.round(Math.abs(s));
  if (secs < 90) return `${secs} s`;
  const min = Math.round(secs / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ""}`;
}

// One scheduled capture as short phrases, e.g. ["Photos 2 of 2", "GNSS fix, 14 satellites", "Burst: dry",
// "Uploaded at once", "Photo 37 s before the pass"].
export function resultParts(r) {
  if (!r) return [];
  if (r.status === "waiting") return ["Waiting for the node's upload"];
  if (r.status === "missed") return ["Missed: no reading from this wake"];
  const parts = [];
  if (r.photo) parts.push(r.photo.of ? `Photos ${r.photo.ok} of ${r.photo.of}` : "No camera status");
  if (r.gnss)
    parts.push(r.gnss.fresh ? `GNSS fix${r.gnss.sats !== null ? `, ${r.gnss.sats} satellites` : ""}` : "No fresh GNSS fix");
  if (r.burst) parts.push(!r.burst.ok ? "Burst failed" : r.burst.state ? `Burst: ${r.burst.state}` : "Burst taken");
  if (r.uploadDelayS !== null) parts.push(r.uploadDelayS <= 90 ? "Uploaded at once" : `Uploaded ${spanText(r.uploadDelayS)} later`);
  if (r.photo && r.lateMs !== null) {
    const what = r.pass ? "the pass" : "its time";
    parts.push(Math.abs(r.lateMs) < 1000 ? `Photo at ${what}` : `Photo ${spanText(r.lateMs / 1000)} ${r.lateMs < 0 ? "before" : "after"} ${what}`);
  }
  const dropped = [...r.dropped].map((c) => TASK_NAMES[c]).filter(Boolean);
  if (dropped.length) parts.push(`Dropped: ${dropped.join(", ")}`);
  return parts;
}

// A thumbnail upright by the camera's saved flips (the Field cameras page's lens settings); "" without them.
export function flipTransform(camera) {
  if (!camera || typeof camera !== "object") return "";
  const x = camera.flip_x === true ? -1 : 1;
  const y = camera.flip_y === true ? -1 : 1;
  return x === 1 && y === 1 ? "" : `scale(${x}, ${y})`;
}

// The satellites a node can wake for, in the server's order, as the card names them.
export const FAMILY_NAMES = { "sentinel-1": "Sentinel-1 (radar)", "sentinel-2": "Sentinel-2", landsat: "Landsat" };

// GET overpasses/plan/: {plan: {enabled, families, days, checked_at, sent_at, problem}, families}.
export function readPassPlan(data) {
  const p = data?.plan;
  if (!p || typeof p !== "object" || typeof p.enabled !== "boolean") return null;
  const all = Array.isArray(data.families) ? data.families.filter((f) => typeof f === "string") : [];
  return {
    enabled: p.enabled,
    families: Array.isArray(p.families) ? p.families.filter((f) => all.includes(f)) : [],
    days: num(p.days),
    checkedAt: str(p.checked_at),
    sentAt: str(p.sent_at),
    problem: str(p.problem),
    all,
  };
}

// POST overpasses/plan/'s answer (a preview or a send): what the schedule gains and loses.
export function readPlanAnswer(data) {
  if (!data || typeof data !== "object" || typeof data.outcome !== "string") return null;
  const words = (v) => (Array.isArray(v) ? v.filter((w) => typeof w === "string") : []);
  return {
    outcome: data.outcome,
    sent: data.sent === true,
    problem: str(data.problem),
    added: (Array.isArray(data.added) ? data.added : []).map(readPass).filter(Boolean),
    skipped: (Array.isArray(data.skipped) ? data.skipped : []).map(readPass).filter(Boolean),
    dropped: words(data.dropped),
    unchanged: data.unchanged === true,
    enabled: data.plan?.enabled === true,
  };
}

// "Sentinel-1 (radar), Landsat"
export const familiesText = (families) => families.map((f) => FAMILY_NAMES[f] || f).join(", ");

// A pass's geometry in a few words: a radar pass by its direction, the angle it looks at the node from and its track,
// an optical one by how far off its track the node is.
export function geometryText(p) {
  if (p.incidenceDeg !== null) {
    const parts = [p.direction || "", `${Math.round(p.incidenceDeg)}° incidence`];
    if (p.track !== null) parts.push(`track ${p.track}`);
    return `${parts.filter(Boolean).join(", ")}${p.edge ? " (near the swath edge)" : ""}`;
  }
  if (p.offNadirKm !== null) return `${p.offNadirKm.toFixed(1)} km off nadir${p.halfSwathKm !== null ? ` of ${p.halfSwathKm}` : ""}`;
  return "–";
}

// ESA's plan for a radar pass (its published acquisition plan): "Planned", "Not planned", "No plan yet"; "–" for an
// optical pass, which images whatever it flies over.
export function esaText(p) {
  if (p.incidenceDeg === null) return "–";
  return p.esa === "planned" ? "Planned" : p.esa === "not_planned" ? "Not planned" : "No plan yet";
}
